import { State, Action, StateContext, Selector } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { PrintersService } from './printers.service';
import { Printer, PrintersStateModel, PrinterTypesResponse } from './printers.model';
import {
  LoadPrinters,
  AddPrinter,
  DeletePrinter,
  LoadPrinterTypes,
  UpdatePrinter,
} from './printers.actions';
import { tap } from 'rxjs';
import { GenericApiResponse } from '../general-store/general.model';
import { NotificationService } from '../../app/services/notification.service';

interface PrintersResponse extends GenericApiResponse {
  payload: Printer[];
}

interface PrinterTypesApiResponse extends GenericApiResponse {
  payload: PrinterTypesResponse;
}

@State<PrintersStateModel>({
  name: 'printers',
  defaults: {
    printers: [],
    printerTypes: null,
    pagination: {
      totalItems: 0,
      totalPages: 0,
      currentPage: 0,
      itemsPerPage: 0,
    },
    headers: [],
    isLoading: false,
    isPrinterTypesLoading: false,
  },
})
@Injectable({ providedIn: 'root' })
export class PrintersState {
  private readonly printersService = inject(PrintersService);
  private readonly notificationService = inject(NotificationService);

  @Selector()
  static getPrinters(state: PrintersStateModel): Printer[] {
    return state.printers;
  }

  @Selector()
  static getPrinterTypes(state: PrintersStateModel) {
    return state.printerTypes;
  }

  @Selector()
  static getPagination(state: PrintersStateModel) {
    return state.pagination;
  }

  @Selector()
  static getHeaders(state: PrintersStateModel) {
    return state.headers;
  }

  @Selector()
  static isLoading(state: PrintersStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static isPrinterTypesLoading(state: PrintersStateModel): boolean {
    return state.isPrinterTypesLoading;
  }

  @Action(LoadPrinters)
  loadPrinters(ctx: StateContext<PrintersStateModel>) {
    ctx.patchState({ isLoading: true });
    return this.printersService.listPrinters().pipe(
      tap((response: PrintersResponse) => {
        ctx.patchState({
          printers: response.payload ?? [],
          pagination: response.pagination ?? {
            totalItems: 0,
            totalPages: 0,
            currentPage: 0,
            itemsPerPage: 0,
          },
          headers: response.headers ?? [],
          isLoading: false,
        });
      }),
    );
  }

  @Action(LoadPrinterTypes)
  loadPrinterTypes(ctx: StateContext<PrintersStateModel>) {
    ctx.patchState({ isPrinterTypesLoading: true });
    return this.printersService.getPrinterTypes().pipe(
      tap((response: PrinterTypesApiResponse) => {
        ctx.patchState({
          printerTypes: response.payload ?? null,
          isPrinterTypesLoading: false,
        });
      }),
    );
  }

  @Action(AddPrinter)
  addPrinter(ctx: StateContext<PrintersStateModel>, action: AddPrinter) {
    ctx.patchState({ isLoading: true });
    return this.printersService.addPrinter(action.payload).pipe(
      tap(() => {
        this.notificationService.openSuccessToast('Printer added successfully');
        ctx.dispatch(new LoadPrinters());
      }),
    );
  }

  @Action(UpdatePrinter)
  updatePrinter(ctx: StateContext<PrintersStateModel>, action: UpdatePrinter) {
    ctx.patchState({ isLoading: true });
    return this.printersService.updatePrinter(action.id, action.payload).pipe(
      tap(() => {
        this.notificationService.openSuccessToast('Printer updated successfully');
        ctx.dispatch(new LoadPrinters());
      }),
    );
  }

  @Action(DeletePrinter)
  deletePrinter(ctx: StateContext<PrintersStateModel>, action: DeletePrinter) {
    ctx.patchState({ isLoading: true });
    return this.printersService.deletePrinter(action.id).pipe(
      tap(() => {
        ctx.dispatch(new LoadPrinters());
      }),
    );
  }
}
